import { useTranslation } from 'react-i18next';
import type { TerminologySummary } from '../types/terminology';

interface StatCardProps {
  label: string;
  value: number;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <div
      style={{
        flex: '1 1 160px',
        background: 'var(--color-surface-elevated)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--spacing-1)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 700,
          color: 'var(--color-brand-primary)',
        }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div
      aria-hidden="true"
      style={{
        flex: '1 1 160px',
        background: 'var(--color-surface-muted)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        minWidth: 0,
        animation: 'pulse 1.5s ease-in-out infinite',
        height: 88,
      }}
    />
  );
}

interface TerminologySummaryCardsProps {
  summary: TerminologySummary | undefined;
  isLoading: boolean;
}

export function TerminologySummaryCards({ summary, isLoading }: TerminologySummaryCardsProps) {
  const { t } = useTranslation();

  const flexRowStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--spacing-4)',
    marginBottom: 'var(--spacing-6)',
  };

  if (isLoading) {
    return (
      <>
        <div style={flexRowStyle}>
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {t('terminologyManagement.dashboard.section.bySource')}
        </div>
        <div style={flexRowStyle}>
          <StatCardSkeleton />
          <StatCardSkeleton />
          <StatCardSkeleton />
        </div>
      </>
    );
  }

  if (!summary) return null;

  return (
    <>
      {/* Main stat cards */}
      <div style={flexRowStyle} role="region" aria-label={t('terminologyManagement.dashboard.title')}>
        <StatCard label={t('terminologyManagement.dashboard.card.totalTerms')} value={summary.totalTerms} />
        <StatCard label={t('terminologyManagement.dashboard.card.autoAddedTerms')} value={summary.autoAddedTerms} />
        <StatCard label={t('terminologyManagement.dashboard.card.totalForbidden')} value={summary.totalForbidden} />
        <StatCard label={t('terminologyManagement.dashboard.card.autoAddedForbidden')} value={summary.autoAddedForbidden} />
      </div>

      {/* Source distribution */}
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--spacing-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {t('terminologyManagement.dashboard.section.bySource')}
      </div>
      <div style={flexRowStyle} role="region" aria-label={t('terminologyManagement.dashboard.section.bySource')}>
        {Object.entries(summary.bySource).map(([source, count]) => (
          <StatCard key={source} label={source} value={count} />
        ))}
      </div>
    </>
  );
}
